import React, { useEffect, useState } from 'react';
import { Card, Button, Badge, EmptyState, ErrorNote } from '../ui';

interface BackupEntry {
  name: string;
  path: string;
  createdAt: string;
  counts: Record<string, number>;
  valid: boolean;
}

export function BackupScreen() {
  const [backups, setBackups] = useState<BackupEntry[]>([]);
  const [dir, setDir] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = async () => {
    try {
      setDir((await window.pagepilot.backup.dir()) as string);
      setBackups((await window.pagepilot.backup.list()) as BackupEntry[]);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => { void load(); }, []);

  return (
    <div className="max-w-4xl space-y-4">
      <h1 className="text-lg font-semibold">Backup & Restore</h1>
      {error && <ErrorNote message={error} />}
      <p className="text-xs text-neutral-500">
        Backups contain configuration and content metadata (pages, prompt templates, schedules, content, asset
        references) with an integrity hash. Secrets (access tokens, API keys) and media files are never included —
        after a restore, reconnect Facebook Pages to re-supply tokens. Backup folder: <span className="font-mono">{dir}</span>
      </p>

      <div className="flex gap-2">
        <Button disabled={busy} onClick={async () => {
          setBusy(true); setMessage(null); setError(null);
          try {
            const r = (await window.pagepilot.backup.create()) as { path: string; counts: Record<string, number> };
            const total = Object.values(r.counts).reduce((a, b) => a + b, 0);
            setMessage(`Backup created (${total} records): ${r.path}`);
            await load();
          } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
          } finally {
            setBusy(false);
          }
        }}>{busy ? 'Backing up…' : 'Create backup now'}</Button>
        <Button variant="ghost" onClick={() => void load()}>Refresh</Button>
      </div>
      {message && <p className="text-xs text-emerald-400">{message}</p>}

      <Card title="Available backups">
        {backups.length === 0 ? (
          <EmptyState title="No backups yet" hint="Create a backup to snapshot your configuration and content metadata." />
        ) : (
          <table className="w-full text-left text-xs">
            <thead className="text-neutral-500">
              <tr>
                <th className="pb-2 pr-3 font-medium">Created</th>
                <th className="pb-2 pr-3 font-medium">Records</th>
                <th className="pb-2 pr-3 font-medium">Integrity</th>
                <th className="pb-2 font-medium">Restore</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b) => {
                const total = Object.values(b.counts).reduce((a, c) => a + c, 0);
                return (
                  <tr key={b.path} className="border-t border-neutral-800/60">
                    <td className="whitespace-nowrap py-2 pr-3 tabular-nums text-neutral-300">{b.createdAt === 'unknown' ? b.name : new Date(b.createdAt).toLocaleString()}</td>
                    <td className="py-2 pr-3 text-neutral-400">{total}</td>
                    <td className="py-2 pr-3"><Badge status={b.valid ? 'HEALTHY' : 'MISCONFIGURED'} label={b.valid ? 'verified' : 'corrupt'} /></td>
                    <td className="py-2">
                      <button
                        disabled={!b.valid || busy}
                        className="text-brand-300 hover:underline disabled:cursor-not-allowed disabled:text-neutral-600"
                        onClick={async () => {
                          if (!confirm('Restore configuration from this backup? Prompt templates, provider settings and per-page settings will be overwritten with the backup\'s values.')) return;
                          setBusy(true); setMessage(null); setError(null);
                          try {
                            const r = (await window.pagepilot.backup.restoreConfig(b.path)) as { restored: Record<string, number>; warnings: string[] };
                            setMessage(`Restored: ${Object.entries(r.restored).map(([k, v]) => `${v} ${k}`).join(', ')}.${r.warnings.length ? ` Warnings: ${r.warnings.join(' | ')}` : ''}`);
                          } catch (e) {
                            setError(e instanceof Error ? e.message : String(e));
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        Restore config
                      </button>
                      <button
                        disabled={!b.valid || busy}
                        className="ml-3 text-neutral-300 hover:underline disabled:cursor-not-allowed disabled:text-neutral-600"
                        onClick={async () => {
                          if (!confirm('Merge content metadata from this backup? Existing records stay unchanged. Secrets and media files are not restored.')) return;
                          setBusy(true); setMessage(null); setError(null);
                          try {
                            const r = (await window.pagepilot.backup.restoreContent(b.path)) as { restored: Record<string, number>; warnings: string[] };
                            setMessage(`Content merged: ${Object.entries(r.restored).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`).join(', ') || 'no new records'}.${r.warnings.length ? ` Warnings: ${r.warnings.join(' | ')}` : ''}`);
                          } catch (e) {
                            setError(e instanceof Error ? e.message : String(e));
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        Merge content
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
